import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CreateIotDevicePayload,
  IotDevice,
  IotDeviceListResponse,
  IotDeviceMeta,
  IotDeviceTypeOption,
} from '../model/iot-device.model';

interface IotDeviceDTO {
  id: number;
  typeId: number;
  name: string;
  status: string;
  webhookOn: string;
  webhookOff: string;
  type?: { id: number; name: string; slug: string };
}

interface IotDeviceListDTO {
  meta: IotDeviceMeta;
  data: IotDeviceDTO[];
}

export interface WebhookValidationResponse {
  isValid: boolean;
  message: string;
  type: string;
}

@Injectable({ providedIn: 'root' })
export class IotDeviceApiService {
  private readonly http = inject(HttpClient);

  getDevices(page: number, maxResults: number): Observable<IotDeviceListResponse> {
    const params = new HttpParams()
      .set('page', page)
      .set('maxResults', maxResults);

    return this.http
      .get<IotDeviceListDTO>(`${environment.baseUrl}/iot-devices`, {
        params,
        headers: this.getHeaders(),
      })
      .pipe(
        map((response) => ({
          meta: response.meta,
          devices: (response.data ?? []).map((device) => this.toModel(device)),
        })),
      );
  }

  getDeviceTypes(): Observable<IotDeviceTypeOption[]> {
    return this.http.get<IotDeviceTypeOption[]>(
      `${environment.baseUrl}/iot-devices/types`,
      { headers: this.getHeaders() },
    );
  }

  createDevice(payload: CreateIotDevicePayload): Observable<unknown> {
    return this.http.post(`${environment.baseUrl}/iot-devices`, payload, {
      headers: this.getHeaders(),
    });
  }

  updateDevice(
    id: string,
    payload: CreateIotDevicePayload,
  ): Observable<unknown> {
    return this.http.put(
      `${environment.baseUrl}/iot-devices/${id}`,
      payload,
      { headers: this.getHeaders() },
    );
  }

  deleteDevice(id: string): Observable<unknown> {
    return this.http.delete(`${environment.baseUrl}/iot-devices/${id}`, {
      headers: this.getHeaders(),
    });
  }

  validateWebhook(url: string): Observable<WebhookValidationResponse> {
    return this.http.post<WebhookValidationResponse>(
      `${environment.baseUrl}/iot-devices/validate-webhook`,
      { url },
      { headers: this.getHeaders() },
    );
  }

  private toModel(dto: IotDeviceDTO): IotDevice {
    return {
      id: String(dto.id),
      typeId: dto.typeId ?? dto.type?.id ?? 0,
      name: dto.name,
      type: dto.type?.name ?? '',
      status: dto.status,
      webhookOn: dto.webhookOn,
      webhookOff: dto.webhookOff,
    };
  }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders({ Authorization: `Bearer ${token || ''}` });
  }
}
